import { EMOJI_SHORTCODES } from "./shortcodes"

export interface EmojiSearchResult {
	name: string
	shortcode: string
	emoji: string
}

const ENTRIES: EmojiSearchResult[] = Object.entries(EMOJI_SHORTCODES).map(
	([shortcode, emoji]) => ({
		name: shortcode.slice(1, -1),
		shortcode,
		emoji,
	}),
)

// Everyday emoji people reach for; they win ties so ":sm" surfaces :smile:
// ahead of the alphabetically-first match.
export const POPULAR_EMOJI_NAMES = [
	"+1",
	"-1",
	"tada",
	"rocket",
	"eyes",
	"heart",
	"fire",
	"smile",
	"joy",
	"pray",
	"raised_hands",
	"wave",
	"white_check_mark",
	"warning",
	"bug",
	"sparkles",
	"thinking_face",
	"sob",
	"100",
	"clap",
]

const POPULARITY = new Map(
	POPULAR_EMOJI_NAMES.map((name, index) => [name, index]),
)

function rank(name: string, query: string): number {
	if (name === query) return 0
	if (name.startsWith(query)) return 1
	if (name.includes(query)) return 2
	return -1
}

export function normalizeEmojiQuery(query: string): string {
	return query.toLowerCase().replace(/[^a-z0-9_+-]/g, "")
}

export function searchEmojis(query: string, limit = 8): EmojiSearchResult[] {
	const normalized = normalizeEmojiQuery(query)
	if (!normalized) return []

	const matches: { entry: EmojiSearchResult; rank: number }[] = []

	for (const entry of ENTRIES) {
		const entryRank = rank(entry.name, normalized)
		if (entryRank === -1) continue
		matches.push({ entry, rank: entryRank })
	}

	return matches
		.sort((a, b) => {
			if (a.rank !== b.rank) return a.rank - b.rank

			const popularityA =
				POPULARITY.get(a.entry.name) ?? POPULAR_EMOJI_NAMES.length
			const popularityB =
				POPULARITY.get(b.entry.name) ?? POPULAR_EMOJI_NAMES.length
			if (popularityA !== popularityB) return popularityA - popularityB

			if (a.entry.name.length !== b.entry.name.length) {
				return a.entry.name.length - b.entry.name.length
			}
			return a.entry.name.localeCompare(b.entry.name)
		})
		.slice(0, limit)
		.map((match) => match.entry)
}

/** Every distinct glyph once, under its first shortcode, in the map's order. */
export function allEmojis(): EmojiSearchResult[] {
	const seen = new Set<string>()
	return ENTRIES.filter((entry) => {
		if (seen.has(entry.emoji)) return false
		seen.add(entry.emoji)
		return true
	})
}

export function getEmojiForShortcodeName(name: string): string | undefined {
	return EMOJI_SHORTCODES[`:${name.toLowerCase()}:`]
}
